import { useCallback } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api/client";
import type {
  HealthieForm,
  HealthieFormModule,
  RequiredIntake,
} from "@/lib/api/hooks";

import { archetypeForModule } from "./IntakeFormField";
import { hydrateHealthieChoiceAnswer } from "./healthieFormRules";
import { intakeDraftKey } from "./useHealthieDraftAutosave";

/**
 * Shared-answer carry-forward between the intakes of one wizard run.
 *
 * The backend tags modules that ask the same question across forms with a
 * `carry_key` (`GET /me/intake/healthie-form/{id}`). Only tagged modules
 * carry, never by wording. Until the field ships it is absent and nothing
 * carries. The type lives here so the patient hook file stays untouched.
 */
export type CarryModule = HealthieFormModule & { carry_key?: string | null };

/** Stored alongside the answers in the target form's draft. */
export interface CarriedMeta {
  /** Product names of the intakes the answers came from, in order. */
  from: string[];
  module_ids: string[];
}

export const CARRIED_KEY = "_carried";

// Consents and e-signatures belong to the form they appear on, and display
// modules have no answer.
const NEVER_CARRY = new Set(["header", "agree", "signature"]);

function carryKeyOf(module: CarryModule): string | null {
  const key = module.carry_key?.trim();
  if (!key) return null;
  return NEVER_CARRY.has(archetypeForModule(module.mod_type)) ? null : key;
}

export function readCarriedMeta(answers: unknown): CarriedMeta | null {
  const raw = (answers as Record<string, unknown> | null | undefined)?.[
    CARRIED_KEY
  ];
  if (!raw || typeof raw !== "object") return null;
  const { from, module_ids } = raw as Record<string, unknown>;
  if (!Array.isArray(module_ids)) return null;
  return {
    from: Array.isArray(from)
      ? from.filter((name): name is string => typeof name === "string")
      : [],
    module_ids: module_ids.filter((id): id is string => typeof id === "string"),
  };
}

/** Fill only empty, tagged fields of `target` from a submitted source form. */
export function carryIntoDraft({
  sourceModules,
  sourceAnswers,
  sourceName,
  targetModules,
  draftAnswers,
}: {
  sourceModules: readonly CarryModule[];
  sourceAnswers: Readonly<Record<string, string>>;
  sourceName: string;
  targetModules: readonly CarryModule[];
  draftAnswers: Readonly<Record<string, unknown>>;
}): Record<string, unknown> | null {
  const carried = new Map<string, { value: string; archetype: string }>();
  for (const module of sourceModules) {
    const key = carryKeyOf(module);
    const value = sourceAnswers[module.id];
    if (key && typeof value === "string" && value.trim())
      carried.set(key, { value, archetype: archetypeForModule(module.mod_type) });
  }
  if (carried.size === 0) return null;

  const filled: Record<string, string> = {};
  for (const module of targetModules) {
    const key = carryKeyOf(module);
    const source = key ? carried.get(key) : undefined;
    if (!source) continue;
    const archetype = archetypeForModule(module.mod_type);
    if (archetype !== source.archetype) continue;
    const existing = draftAnswers[module.id];
    if (typeof existing === "string" && existing.trim()) continue;
    // Same normalization the renderer applies when it restores a draft.
    const value =
      archetype === "radio" || archetype === "checkbox"
        ? hydrateHealthieChoiceAnswer(module, source.value)
        : source.value;
    if (value) filled[module.id] = value;
  }
  const ids = Object.keys(filled);
  if (ids.length === 0) return null;

  const previous = readCarriedMeta(draftAnswers);
  const meta: CarriedMeta = {
    from: [...new Set([...(previous?.from ?? []), sourceName])],
    module_ids: [...new Set([...(previous?.module_ids ?? []), ...ids])],
  };
  return { ...draftAnswers, ...filled, [CARRIED_KEY]: meta };
}

type DraftResponse = {
  form_slug: string;
  answers: Record<string, unknown>;
  updated_at: string | null;
};

async function carryIntoForm(
  queryClient: QueryClient,
  source: SubmittedIntake,
  targetFormId: string,
  { signal, writeBy }: { signal: AbortSignal; writeBy: number },
) {
  // Same cache entry as useHealthieForm, so the next form renders instantly.
  const form = await queryClient.fetchQuery({
    queryKey: ["me", "healthie-form", targetFormId, { personalize: true }],
    queryFn: () =>
      apiFetch<HealthieForm>(
        `/me/intake/healthie-form/${targetFormId}?personalize=true`,
        { signal },
      ),
    staleTime: 10 * 60_000,
  });
  const slug = `healthie-${targetFormId}`;
  const draft = await apiFetch<DraftResponse>(`/me/intake/draft/${slug}`, {
    signal,
  });
  const answers = carryIntoDraft({
    sourceModules: source.modules,
    sourceAnswers: source.answers,
    sourceName: source.productName,
    targetModules: form.modules,
    draftAnswers: draft.answers ?? {},
  });
  // Too late to write: the wizard may already have opened this form, whose
  // own saves must not be replaced by this older snapshot.
  if (!answers || signal.aborted || Date.now() > writeBy) return;
  // PUT replaces the draft, so send the existing answers back with the new ones.
  await apiFetch(`/me/intake/draft/${slug}`, {
    method: "PUT",
    body: JSON.stringify({ answers }),
    signal,
  });
  queryClient.setQueryData(intakeDraftKey(slug), {
    form_slug: slug,
    answers,
    updated_at: new Date().toISOString(),
  });
}

export interface SubmittedIntake {
  formId: string;
  productName: string;
  modules: readonly CarryModule[];
  answers: Readonly<Record<string, string>>;
}

// Bundle legs are provisioned seconds apart; separate checkouts are not.
const SAME_CHECKOUT_MS = 10 * 60_000;

/**
 * The later intakes of the wizard run that belong to the same purchase as
 * `source`. The wizard also queues older pending orders, so its order alone
 * is not a purchase boundary. A Shop purchase names its orders (`?orders=`);
 * otherwise orders created within minutes of each other count as one
 * checkout. Renewal check-ins and clinician-requested corrections always
 * start blank, so only first treatment intakes give or receive answers.
 */
export function sameCheckoutIntakes(
  source: RequiredIntake,
  later: readonly RequiredIntake[],
  purchasedOrderIds: readonly string[],
): RequiredIntake[] {
  if (source.intake_kind !== "treatment_intake") return [];
  const sourceAt = Date.parse(source.created_at);
  const inPurchase = (intake: RequiredIntake) =>
    purchasedOrderIds.length > 0
      ? purchasedOrderIds.includes(intake.awaiting_order_id)
      : Math.abs(Date.parse(intake.created_at) - sourceAt) <= SAME_CHECKOUT_MS;
  if (!inPurchase(source)) return [];
  return later.filter(
    (intake) =>
      intake.intake_kind === "treatment_intake" &&
      !intake.intake_submitted_at &&
      inPurchase(intake),
  );
}

// Reads may take this long before the carry gives up on writing; once a
// write has started, the wizard waits for it so nothing lands afterwards.
const CARRY_WRITE_BY_MS = 4_000;
const CARRY_ABORT_MS = 10_000;

/**
 * Seed the remaining forms of this purchase with the answers just
 * submitted. Best effort: a failure or a slow backend never holds the
 * patient on the submitted form for long.
 */
export function useIntakeCarry() {
  const queryClient = useQueryClient();
  return useCallback(
    async (source: SubmittedIntake, targetFormIds: readonly string[]) => {
      const targets = [...new Set(targetFormIds)].filter(
        (id) => id !== source.formId,
      );
      if (targets.length === 0) return;
      // Nothing is tagged: skip the round trips entirely.
      if (!source.modules.some((module) => carryKeyOf(module))) return;
      const controller = new AbortController();
      const abort = setTimeout(() => controller.abort(), CARRY_ABORT_MS);
      const writeBy = Date.now() + CARRY_WRITE_BY_MS;
      await Promise.allSettled(
        targets.map((id) =>
          carryIntoForm(queryClient, source, id, {
            signal: controller.signal,
            writeBy,
          }),
        ),
      );
      clearTimeout(abort);
    },
    [queryClient],
  );
}
